import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";

import { useCurrentUserContext } from "@contexts/current-user-provider";

import { useLazyQuery } from "@stadium/services/apollo-client";
import { useStoreContext } from "@components/store/provider";
import { useCreateShopContext } from "@contexts/create-shop-context";

import { fetchMenu } from "@graphql/queries/stores/menu/main";

import { getMenu, getPricelessMenuVariables } from "@selectors/menu";
import { getCommonGlobalCountryVariables, getMenuItemsCountry } from "@helpers/store";
import { serializeMenu } from "@helpers/menu";

import { getIsLegacyLockerShop, getIsLockerShop, getStore, getStoreNumber } from "@selectors/store";
import { getCountry } from "@selectors/country";
import { setMenu } from "@slices/menu";

import {
  CLOUDINARY_ATTACHMENT_HEIGHT,
  CLOUDINARY_ATTACHMENT_WIDTH,
  MENU_INTENT,
  SWAGMAGIC_INTENT,
} from "@constants/menu";
const defaultObj = {};
const DEFAULT_FALLBACKS = {
  isGlobalCountry: false,
  isNewShopCreationFlow: false,
};

const useFetchMenu = ({
  skip,
  additionalVariables = defaultObj,
  noPriceRangeRestriction,
  defaultMenuData,
  requireAuth = false,
  storeNumber: storeNumberFromProps = null,
  fallbacks = DEFAULT_FALLBACKS,
  isSwagCreationShopFlow = false,
}) => {
  const dispatch = useDispatch();
  const country = useSelector(getCountry);
  const store = useSelector(getStore);
  const storeNumber = useSelector(getStoreNumber);
  const { isGlobalCountry } = useStoreContext();
  const orderedMenuItem = useSelector(getMenu);
  const pricelessMenuvariables = useSelector(getPricelessMenuVariables);
  const isLegacyLockerShop = useSelector(getIsLegacyLockerShop);
  const isLockerShop = useSelector(getIsLockerShop);

  const {
    currentUserData: { email: isAuthenticated },
    loading: isAuthenticationLoading,
  } = useCurrentUserContext();

  const { isNewShopCreationFlow: defaultIsNewShopCreationFlow } = useCreateShopContext();

  const isNewShopCreationFlow = defaultIsNewShopCreationFlow || fallbacks.isNewShopCreationFlow;

  const commonGlobalCountryVariables = getCommonGlobalCountryVariables({
    country,
    isGlobalCountry: isGlobalCountry || fallbacks.isGlobalCountry,
  });

  const newShopCreationFlowVariables = useMemo(() => {
    if (!isNewShopCreationFlow) return {};
    return {
      includeDeactivatedProducts: true,
      includeDisabledTaxons: false,
      includeLockedSp: true,
    };
  }, [isNewShopCreationFlow]);

  const variables = {
    location: "eshop-main",
    intent: isLegacyLockerShop ? SWAGMAGIC_INTENT : MENU_INTENT,
    storeNumber: storeNumberFromProps ?? storeNumber,
    menuItemAttachmentHeight: CLOUDINARY_ATTACHMENT_HEIGHT,
    menuItemAttachmentWidth: CLOUDINARY_ATTACHMENT_WIDTH,
    ...commonGlobalCountryVariables,
    ...newShopCreationFlowVariables,
    ...(noPriceRangeRestriction ? defaultObj : pricelessMenuvariables),
    menuItemsCountry: getMenuItemsCountry({
      isSwagCreationShopFlow,
      country,
      isGlobalCountry: isGlobalCountry || fallbacks.isGlobalCountry,
    }),
    ...additionalVariables,
  };
  const [fetchMenuData, { data: menuData, loading, called }] = useLazyQuery(fetchMenu, {
    variables,
    fetchPolicy: "no-cache",
  });

  const data = menuData || defaultMenuData;

  useEffect(() => {
    if (isAuthenticationLoading || (requireAuth && !isAuthenticated)) return;

    if (
      // fetch to get the authenticated menu items such as my designed swag
      (!called && isLockerShop && isAuthenticated) ||
      (!skip && !defaultMenuData)
    ) {
      fetchMenuData();
    }
  }, [country?.iso, storeNumber, skip, isAuthenticated, pricelessMenuvariables]);

  const serializedMenu = useMemo(() => {
    return serializeMenu({ data, countryIso: country?.iso, store, isNewShopCreationFlow });
  }, [data, isNewShopCreationFlow]);

  useEffect(() => {
    if (!noPriceRangeRestriction && (data || serializedMenu.fetchedOrderedMenuItemId)) {
      if (isEqual(omit(orderedMenuItem, "isLoaded"), serializedMenu)) return;
      dispatch(setMenu(serializedMenu));
    }
  }, [dispatch, serializedMenu]);

  return useMemo(
    () => ({
      loading,
      menuData: serializedMenu,
      fetchMenuData,
    }),
    [loading, serializedMenu, fetchMenuData],
  );
};

export default useFetchMenu;
